import { useForm } from "react-hook-form";
import { Link, Navigate } from "react-router-dom";
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@learn-me/ui";
import { useAuth } from "@/features/auth/hooks/useAuth";
import type { RegisterRequest } from "@learn-me/types";
import TermsCheckbox from "@/components/legal/TermsCheckbox";
import {
  LEGAL_TERMS_VERSION,
  LEGAL_PRIVACY_VERSION,
} from "@/content/legal/versions";

type RegisterFormValues = RegisterRequest & {
  confirmPassword: string;
  acceptTerms: boolean;
};

interface PasswordCheck {
  label: string;
  passed: boolean;
}

function evaluatePassword(value: string): PasswordCheck[] {
  return [
    { label: "Mínimo 8 caracteres", passed: value.length >= 8 },
    { label: "Una letra mayúscula (A-Z)", passed: /[A-Z]/.test(value) },
    { label: "Una letra minúscula (a-z)", passed: /[a-z]/.test(value) },
    { label: "Un número (0-9)", passed: /[0-9]/.test(value) },
  ];
}

export default function RegisterPage() {
  const {
    register: registerUser,
    registerLoading,
    isAuthenticated,
  } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterFormValues>({
    defaultValues: { acceptTerms: false },
  });

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = (data: RegisterFormValues) => {
    const { confirmPassword: _confirm, acceptTerms: _accept, ...rest } = data;
    registerUser({
      ...rest,
      termsAcceptance: {
        termsVersion: LEGAL_TERMS_VERSION,
        privacyVersion: LEGAL_PRIVACY_VERSION,
        contextPath: "/register",
      },
    });
  };

  const password = watch("password") ?? "";
  const passwordChecks = evaluatePassword(password);

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <h1 className="text-2xl font-bold">Crear Cuenta</h1>
          <p className="text-muted-foreground">
            Regístrate para acceder a todos los cursos
          </p>
        </CardHeader>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CardContent className="space-y-4">
            <Input
              label="Nombre"
              type="text"
              placeholder="Tu nombre"
              error={errors.firstName?.message}
              {...register("firstName", {
                required: "El nombre es requerido",
                minLength: { value: 2, message: "Mínimo 2 caracteres" },
              })}
            />
            <Input
              label="Apellido"
              type="text"
              placeholder="Tu apellido"
              error={errors.lastName?.message}
              {...register("lastName", {
                required: "El apellido es requerido",
                minLength: { value: 2, message: "Mínimo 2 caracteres" },
              })}
            />
            <Input
              label="Correo electrónico"
              type="email"
              placeholder="tu@email.com"
              error={errors.email?.message}
              {...register("email", {
                required: "El correo es requerido",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Correo inválido",
                },
              })}
            />
            <div>
              <Input
                label="Contraseña"
                type="password"
                placeholder="********"
                error={errors.password?.message}
                {...register("password", {
                  required: "La contraseña es requerida",
                  minLength: { value: 8, message: "Mínimo 8 caracteres" },
                  validate: (value) => {
                    if (!/[A-Z]/.test(value))
                      return "Debe incluir una letra mayúscula";
                    if (!/[a-z]/.test(value))
                      return "Debe incluir una letra minúscula";
                    if (!/[0-9]/.test(value)) return "Debe incluir un número";
                    return true;
                  },
                })}
              />
              <ul className="mt-2 space-y-1 text-xs">
                {passwordChecks.map((check) => (
                  <li
                    key={check.label}
                    className={
                      check.passed
                        ? "text-green-600 flex items-center gap-1"
                        : "text-muted-foreground flex items-center gap-1"
                    }
                  >
                    <span aria-hidden="true">{check.passed ? "✓" : "○"}</span>
                    {check.label}
                  </li>
                ))}
              </ul>
            </div>
            <Input
              label="Confirmar contraseña"
              type="password"
              placeholder="********"
              error={errors.confirmPassword?.message}
              {...register("confirmPassword", {
                required: "Confirma tu contraseña",
                validate: (value) =>
                  value === password || "Las contraseñas no coinciden",
              })}
            />
            <TermsCheckbox
              {...register("acceptTerms", {
                required:
                  "Debes aceptar los Términos y la Política de Privacidad",
              })}
              error={errors.acceptTerms?.message}
            />
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full"
              isLoading={registerLoading}
            >
              Crear Cuenta
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-primary hover:underline">
                Inicia sesión
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
